import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { broadcast, inviteCodes, notifications } from '../services/api';
import LayoutV2 from '../components/LayoutV2';
import ProgressStatsSection from '../components/dashboard/ProgressStatsSection';
import config from '../config';
import {
  ActionPanel,
  Button,
  Loading,
  PageHeader,
  SectionBlock
} from '../components/ui';

function InviteCodePanel({ codes }) {
  const [copied, setCopied] = useState('');

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(''), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  return (
    <SectionBlock
      title="My Referral Codes"
    >
      {codes.length === 0 ? (
        <div className="rounded-[20px] border border-dashed border-white/10 bg-slate-950/20 p-6 text-sm text-slate-400">
          No invite codes are currently assigned to this account.
        </div>
      ) : (
        <div className="flex flex-wrap gap-3">
          {codes.map((code) => (
            <button
              key={code.id}
              type="button"
              onClick={() => copyCode(code.invite_code)}
              className="rounded-full border border-blue-200/18 bg-blue-300/10 px-4 py-2 text-sm font-medium text-blue-100 transition-colors hover:bg-blue-300/16"
            >
              <span className="font-mono">{code.invite_code}</span>
              <span className="ml-2 text-xs text-slate-300">{copied === code.invite_code ? 'Copied' : 'Copy'}</span>
            </button>
          ))}
        </div>
      )}
    </SectionBlock>
  );
}

export default function DashboardV2() {
  const { user, isAdmin, isOperations } = useAuth();
  const [inviteCodesList, setInviteCodesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedTextKey, setCopiedTextKey] = useState('');
  const [inviteCodeStatus, setInviteCodeStatus] = useState(null);
  const [broadcastState, setBroadcastState] = useState({ message: '', isVisible: false });
  const [broadcastDraft, setBroadcastDraft] = useState({ message: '', isVisible: false });

  useEffect(() => {
    if (!user?.id) return;

    const loadStats = async () => {
      try {
        if (!isAdmin) {
          try {
            const codesRes = await inviteCodes.list();
            setInviteCodesList(codesRes.data.inviteCodes || []);

            const statusRes = await inviteCodes.getMyCode();
            setInviteCodeStatus(statusRes.data);
          } catch (err) {
            console.error('Failed to load invite-code dashboard data:', err);
          }
        }

        const broadcastRes = await broadcast.get();
        const nextBroadcast = {
          message: String(broadcastRes.data.message || ''),
          isVisible: broadcastRes.data.isVisible === true
        };
        setBroadcastState(nextBroadcast);
        setBroadcastDraft(nextBroadcast);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [user?.id, isAdmin, isOperations]);

  useEffect(() => {
    if (isAdmin || isOperations || !user?.id) return;
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    const token = `web-${user.id}-${navigator.userAgent.slice(0, 24)}`;
    notifications.subscribe({ token, platform: 'web' }).catch(() => {});
  }, [isAdmin, isOperations, user?.id]);

  const inviteCode = inviteCodesList[0]?.invite_code || 'YOUR_INVITE_CODE';
  const publicPath = (config.publicUrl || '').replace(/\/$/, '');
  const inviteLink = `${window.location.origin}${publicPath}/?register=1&referrer=${encodeURIComponent(user?.username || '')}`;
  const referCustomerText = `Visit https://www.creditkaagapay.com/, download the app, and use referral code ${inviteCode} when you register.`;
  const canInviteNetwork = user?.tier === 1 || user?.tier === 2;
  const growNetworkText = canInviteNetwork
    ? `Use my signup link to join Octopus with my username prefilled: ${inviteLink}`
    : 'You are not eligible to invite users yet. Contact your admin to request an upgrade.';

  const copyText = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedTextKey(key);
      setTimeout(() => setCopiedTextKey(''), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const roleTasks = {
    0: [
      'Manage users at /admin/users.',
      'Review admin customer data at /admin/customers.',
      'Monitor statements and settlements at /statements.',
      'Maintain invite codes at /admin/invite-codes.'
    ],
    1: [
      'Track team earnings at /earnings.',
      'Review direct reports at /network.',
      'Review customer performance at /customers.',
      'Check statements and payout status at /statements.',
      'Invite qualified users with your referral flow from /dashboard.'
    ],
    2: [
      'Monitor your earnings at /earnings.',
      'Manage your downline structure at /network.',
      'Follow customer progress at /customers.',
      'Track statement status at /statements.',
      'Share your invite link from /dashboard.'
    ],
    3: [
      'Track your own earnings at /earnings.',
      'Review your customer list at /customers.',
      'Check settlement history at /statements.',
      'View your available invite codes on /dashboard.',
      'Request role upgrade from admin when you need network-invite access.'
    ],
    4: [
      'Manage users at /admin/users.',
      'Review admin customer data at /admin/customers.',
      'Maintain invite codes at /admin/invite-codes.'
    ]
  };

  const currentRoleTasks = roleTasks[user?.tier] || [];

  const saveBroadcast = async () => {
    try {
      const res = await broadcast.update(broadcastDraft);
      const nextBroadcast = {
        message: String(res.data.message || ''),
        isVisible: res.data.isVisible === true
      };
      setBroadcastState(nextBroadcast);
      setBroadcastDraft(nextBroadcast);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save broadcast');
    }
  };

  return (
    <LayoutV2>
      <PageHeader
        title="Dashboard"
      />

      <ProgressStatsSection />

      {broadcastState.isVisible && broadcastState.message ? (
        <div className="mb-6 rounded-[22px] border border-amber-300/18 bg-amber-300/10 px-4 py-3 text-sm text-amber-100">
          {broadcastState.message}
        </div>
      ) : null}

      {loading ? (
        <Loading text="Loading dashboard..." />
      ) : (
        <div className="space-y-6">
          {isAdmin || isOperations ? (
            <>
              <SectionBlock
                title="Broadcast Message"
                actions={
                  <Button type="button" variant="primary" onClick={saveBroadcast}>
                    Save Broadcast
                  </Button>
                }
              >
                <textarea
                  value={broadcastDraft.message}
                  maxLength={140}
                  onChange={(e) => setBroadcastDraft((prev) => ({ ...prev, message: e.target.value }))}
                  className="input min-h-[144px] resize-none"
                />
                <label className="mt-4 flex items-center gap-3 rounded-[18px] border border-white/8 bg-white/[0.03] px-4 py-3 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={broadcastDraft.isVisible}
                    onChange={(e) => setBroadcastDraft((prev) => ({ ...prev, isVisible: e.target.checked }))}
                  />
                  Show pinned message to users
                </label>
              </SectionBlock>
              <SectionBlock title="What can I do in this system?">
                <ul className="space-y-3 text-sm text-slate-300">
                  {currentRoleTasks.map((task) => (
                    <li key={task} className="rounded-[18px] border border-white/8 bg-white/[0.03] px-4 py-3">{task}</li>
                  ))}
                </ul>
              </SectionBlock>
            </>
          ) : !isOperations ? (
            <>
              <SectionBlock title="Invitation">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <ActionPanel
                    title="Refer a customer"
                    value={referCustomerText}
                    action={
                      <Button type="button" variant="secondary" onClick={() => copyText('refer', referCustomerText)}>
                        {copiedTextKey === 'refer' ? 'Copied' : 'Copy'}
                      </Button>
                    }
                  />
                  <ActionPanel
                    title="Grow my network"
                    value={growNetworkText}
                    action={
                      <Button type="button" variant="secondary" onClick={() => copyText('network', growNetworkText)}>
                        {copiedTextKey === 'network' ? 'Copied' : 'Copy'}
                      </Button>
                    }
                  />
                </div>
              </SectionBlock>
              <InviteCodePanel codes={inviteCodesList} />
              {!isAdmin && inviteCodeStatus && !inviteCodeStatus.hasInviteCode ? (
                <div className="rounded-[22px] border border-red-300/18 bg-red-300/10 px-4 py-3 text-sm text-red-100">
                  {inviteCodeStatus.message || 'Unable to refer customers: please contact your upline or administrator to get an invite code.'}
                </div>
              ) : null}
              <SectionBlock title="What can I do in this system?">
                <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {currentRoleTasks.map((task) => (
                    <li key={task} className="rounded-[18px] border border-white/8 bg-white/[0.03] px-4 py-3 text-sm text-slate-200">{task}</li>
                  ))}
                </ul>
              </SectionBlock>
            </>
          ) : null}
          {isAdmin || isOperations ? null : <div className="hidden" data-testid="dashboard-invite-code-status">{inviteCodeStatus?.hasInviteCode ? 'has-code' : 'no-code'}</div>}
        </div>
      )}
    </LayoutV2>
  );
}
